import React, { useEffect, useState, useRef } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Col, Row, FormGroup, Label, Input, Button, Card, CardBody, CardHeader } from "reactstrap";
import { useEtim } from "../../context/ETIMContext";
import { useService } from "../../context/ServiceContext";
import PaginatedSelect from "./PaginatedSelect";
import "./EtimSelection.css";

const FeatureList = ({ features, sharedFeatures }) => {
  const sharedStyle = {
    backgroundColor: "#e2efda",
    paddingTop: "3px",
    paddingBottom: "3px",
    borderRadius: "15px",
    //paddingLeft: "15px",
  };
  const bdpStyle = {
    color: "red",
    paddingTop: "3px",
    paddingBottom: "3px",
    //padding: "3px 0px 3px 15px",
    //paddingLeft: "15px",
  };

  return (
    <Card>
      <CardBody>
        <ul style={{ listStyle: "none", padding: 0 }}>
          {features.map((feature) => {
            const isSharedFeature = sharedFeatures.includes(feature.featureId);
            const isDataFromBdP = feature.isDataFromBdP;
            return (
              <li
                key={feature.featureId}
                style={{
                  //sscolor: sharedFeatures.includes(feature.featureId) ? "green" : feature.isDataFromBdP ? "red" : "black",
                  marginBottom: "5px",
                  ["padding-left"]: "15px",
                  ...(isSharedFeature ? sharedStyle : {}),
                  ...(isDataFromBdP ? bdpStyle : {}),
                }}
              >
                {feature.description}
              </li>
            );
          })}
        </ul>
      </CardBody>
    </Card>
  );
};

export default function EtimSelection({ handleSelectionNext }) {
  const { etim, setEtim, etimClasses, versions, features, setFeatures, setIsDirty } = useEtim();

  const [sharedFeatures, setSharedFeatures] = useState([]);
  const { etimFeaturesProvider, etimCommonFeaturesProvider, etimClassSeriesProvider } = useService();
  const prevComp1Ref = useRef();
  const prevVersionRef = useRef();
  const intl = useIntl();

  useEffect(() => {
    const fetchSeriesClassEtim = async () => {
      const etimClass = await etimClassSeriesProvider.get({ seriesId: etim.seriesId });
      if (etimClass) {
        setEtim({
          type: "SET_CLASS_ETIM",
          payload: etimClass,
        });
        setEtim({ type: "SET_COMPARISON1", payload: etimClass });
      }
    };

    if (etim.comparison1 == null || etim.comparison1 === "") {
      fetchSeriesClassEtim();
    }
  }, [etim.seriesId]);

  useEffect(() => {
    const fetchSelectedFeatures = async () => {
      if (!etim.comparison1) {
        setFeatures((prev) => ({ ...prev, selected: [] }));
        return;
      }

      const shouldFetch = etim.comparison1 !== prevComp1Ref.current || etim.version !== prevVersionRef.current;

      if (!shouldFetch) return;

      const selectedFeatures = await etimFeaturesProvider.get({ version: etim.version, id: etim.comparison1 });
      setFeatures((prev) => ({
        ...prev,
        comp1: selectedFeatures,
      }));

      prevComp1Ref.current = etim.comparison1;
      prevVersionRef.current = etim.comparison1;
    };

    fetchSelectedFeatures();
  }, [etim.comparison1, etim.version]);

  useEffect(() => {
    const fetchComp2Features = async () => {
      if (etim.comparison2) {
        const comp1Features = await etimFeaturesProvider.get({ version: etim.version, id: etim.comparison2 });
        setFeatures((prev) => ({
          ...prev,
          comp2: comp1Features,
        }));
      } else {
        setFeatures((prev) => ({ ...prev, comp1: [] }));
      }
    };

    fetchComp2Features();
  }, [etim.comparison2, etim.version]);

  useEffect(() => {
    const fetchComp3Features = async () => {
      if (etim.comparison3) {
        //  const shouldFetch = etim.comparison2 !== prevComp2Ref.current || etim.version !== prevVersionRef.current;
        //  if (shouldFetch) {
        const comp2Features = await etimFeaturesProvider.get({ version: etim.version, id: etim.comparison3 });
        setFeatures((prev) => ({
          ...prev,
          comp3: comp2Features,
        }));
        //  prevComp2Ref.current = etim.comparison2;
        //  }
      } else {
        setFeatures((prev) => ({ ...prev, comp2: [] }));
      }
    };

    fetchComp3Features();
  }, [etim.comparison3, etim.version]);

  useEffect(() => {
    const fetchSharedFeaturesData = async () => {
      const claseIds = [etim.comparison1, etim.comparison2, etim.comparison3].filter(Boolean);
      if (claseIds.length > 1) {
        const shared = await etimCommonFeaturesProvider.get({ version: etim.version, etimClasses: claseIds });
        setSharedFeatures(shared.map((f) => f.featureId));
      } else {
        setSharedFeatures([]);
      }
    };

    fetchSharedFeaturesData();
  }, [etim.comparison1, etim.comparison2, etim.comparison3, etim.version, etimCommonFeaturesProvider, setSharedFeatures]);

  const validateSelectedClassEtim = (classEtim, setDefault) => {
    let isValid = etim.classEtim === classEtim || etim.classEtim === classEtim || etim.classEtim === classEtim;
    if (!isValid && setDefault) {
      setEtim({ type: "SET_CLASS_ETIM", payload: classEtim });
    }
  };

  const handleVersionChange = (e) => {
    const version = e.target.value;
    setEtim({ type: "CLEAR_GROUPED_PRODUCTS" });

    setEtim({ type: "SET_VERSION", payload: version });
  };

  const handleComparison1Change = (option) => {
    const classId = option.value;

    setEtim({ type: "SET_COMPARISON1", payload: classId });

    validateSelectedClassEtim(classId, true);
  };

  const handleComparison2Change = (option) => {
    const classId = option.value;
    setEtim({ type: "SET_COMPARISON2", payload: classId });
    validateSelectedClassEtim(classId);
  };

  const handleComparison3Change = (option) => {
    const classId = option.value;
    setEtim({ type: "SET_COMPARISON3", payload: classId });
    validateSelectedClassEtim(classId);
  };

  const handleSelectFinal = (e) => {
    const claseId = e.target.value;
    setIsDirty(true);
    setEtim({ type: "SET_CLASS_ETIM", payload: claseId }); // Only update classEtim via radio
  };

  const legendItems = [
    {
      color: "#e2efda",
      id: "etim.shared_feature",
      defaultMessage: "Shared Feature",
    },
    {
      color: "red",
      id: "etim.data_from_bdp",
      defaultMessage: "Data from BdP",
    },
  ];

  return (
    <div className="etim-selection">
      <Card>
        <CardHeader>
          <h2>
            <FormattedMessage id="etim.select_title" defaultMessage="Select ETIM Class *" />
          </h2>
        </CardHeader>
        <CardBody>
          <Row>
            <Col md="4">
              <FormGroup>
                <Label for="etimVersion">
                  <FormattedMessage id="etim.etim_version" defaultMessage="ETIM Version" />
                </Label>
                <Input type="select" id="etimVersion" value={etim.version} onChange={handleVersionChange}>
                  {versions.map((version) => (
                    <option key={version.version} value={version.version}>
                      {version.version}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md="8" className="flex align-content-center">
              <div className="legend-container">
                {legendItems.map((item, index) => (
                  <div key={index} className="legend-item" role="presentation" aria-label={`${item.defaultMessage}`}>
                    <span className="legend-rectangle" style={{ backgroundColor: item.color }} aria-hidden="true" />
                    <span className="legend-label">
                      <FormattedMessage id={item.id} defaultMessage={item.defaultMessage} />
                    </span>
                  </div>
                ))}
              </div>
            </Col>
          </Row>

          <Row>
            <Col md="4">
              <FormGroup>
                <Label for="claseEtim">
                  <FormattedMessage id="app.etim_class" defaultMessage="ETIM Class" />
                </Label>
                <PaginatedSelect
                  options={etimClasses}
                  placeholder={intl.formatMessage({ id: "etim.select_title", defaultMessage: "Select ETIM Class" })}
                  value={etim.comparison1}
                  handleChange={handleComparison1Change}
                />
              </FormGroup>
              {etim.comparison1 && <FeatureList features={features.comp1} sharedFeatures={sharedFeatures} />}
            </Col>
            <Col md="4">
              <FormGroup>
                <Label for="comparison2">
                  <FormattedMessage id="etim.comparison_1" defaultMessage="Comparison 1" />
                </Label>
                <PaginatedSelect
                  options={etimClasses}
                  placeholder={intl.formatMessage({ id: "etim.select_title", defaultMessage: "Select ETIM Class" })}
                  value={etim.comparison2}
                  handleChange={handleComparison2Change}
                />
              </FormGroup>
              {etim.comparison2 && <FeatureList features={features.comp2} sharedFeatures={sharedFeatures} />}
            </Col>
            <Col md="4">
              <FormGroup>
                <Label for="comparison3">
                  <FormattedMessage id="etim.comparison_2" defaultMessage="Comparison 2" />
                </Label>
                <PaginatedSelect
                  options={etimClasses}
                  placeholder={intl.formatMessage({ id: "etim.select_title", defaultMessage: "Select ETIM Class" })}
                  value={etim.comparison3}
                  handleChange={handleComparison3Change}
                />
              </FormGroup>
              {etim.comparison3 && <FeatureList features={features.comp3} sharedFeatures={sharedFeatures} />}
            </Col>
          </Row>
          <Row className="mt-3">
            <Col>
              <Card>
                <CardHeader>
                  <Label>
                    <FormattedMessage id="etim.select_final_etim" defaultMessage="Select Final ETIM Class" />
                  </Label>
                </CardHeader>
                <CardBody>
                  <FormGroup>
                    <div>
                      <FormGroup check className="mb-2">
                        <Label check for="comp1">
                          <Input
                            type="radio"
                            id="comp1"
                            name="finalClaseEtim"
                            value={etim.comparison1}
                            checked={etim.classEtim === etim.comparison1}
                            onChange={handleSelectFinal}
                            disabled={!etim.comparison1}
                          />
                          {etimClasses.find((e) => e.value === etim.comparison1)?.label || intl.formatMessage({ id: "etim.select_1_etim", defaultMessage: "Select 1 ETIM class" })}
                        </Label>
                      </FormGroup>
                      {etim.comparison2 && (
                        <FormGroup check className="mb-2">
                          <Label check for="comp2">
                            <Input
                              type="radio"
                              id="comp2"
                              name="finalClaseEtim"
                              value={etim.comparison2}
                              checked={etim.classEtim === etim.comparison2}
                              onChange={handleSelectFinal}
                            />
                            {etimClasses.find((e) => e.value === etim.comparison2)?.label || intl.formatMessage({ id: "etim.comparison_2", defaultMessage: "Comparison 2" })}
                          </Label>
                        </FormGroup>
                      )}
                      {etim.comparison3 && (
                        <FormGroup check className="mb-2">
                          <Input
                            type="radio"
                            id="comp3"
                            name="finalClaseEtim"
                            value={etim.comparison3}
                            checked={etim.classEtim === etim.comparison3}
                            onChange={handleSelectFinal}
                          />
                          <Label check for="comp3">
                            {etimClasses.find((e) => e.value === etim.comparison3)?.label || intl.formatMessage({ id: "etim.selected", defaultMessage: "Selected" })}
                          </Label>
                        </FormGroup>
                      )}
                    </div>
                  </FormGroup>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Button className="mt-3" color="primary" onClick={handleSelectionNext} disabled={!etim.classEtim}>
            <FormattedMessage id="app.next" defaultMessage="Next" />
          </Button>
        </CardBody>
      </Card>
    </div>
  );
}
